import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

/**
 * Three pricing cards (Free / Premium Monthly / Premium Annual) shared by
 * the public Plans page and the member Billing page.
 *
 * mode:
 *   "public"  – buttons are <Link>s to /register (or /register?plan=...).
 *   "member"  – buttons call the provided handlers so we can go through the
 *               existing Stripe checkout / portal flows without duplicating logic.
 *
 * Member-mode props:
 *   currentPlan         – "free" | "premium"
 *   currentLookupKey    – "premium_monthly" | "premium_annual" | null
 *   onSelectMonthly()   – required in member mode (calls existing upgrade())
 *   onSelectAnnual()    – required in member mode
 *   onSwitchPlan()      – optional, called when a premium member clicks
 *                         "Switch to Monthly/Annual". Defaults to onSelectX.
 *   busy, error         – optional
 */
export function PricingCards({
  mode = "public",
  currentPlan = null,
  currentLookupKey = null,
  onSelectMonthly,
  onSelectAnnual,
  onSwitchPlan,
  busy = false,
  error = "",
}) {
  const monthlyTotal = 4.95 * 12;
  const savingsPct = Math.round((1 - 49.95 / monthlyTotal) * 100);

  const isMember = mode === "member";
  const isFree = currentPlan === "free";
  const isPremium = currentPlan === "premium";
  const isMonthly = isPremium && currentLookupKey === "premium_monthly";
  const isAnnual = isPremium && currentLookupKey === "premium_annual";
  const switchHandler = onSwitchPlan;

  // --- Free CTA ---
  let freeCta;
  if (!isMember) {
    freeCta = (
      <Link to="/register"><Button variant="outline" className="mt-8 w-full h-11 border-slate-300" data-testid="free-start-btn">Start Free</Button></Link>
    );
  } else if (isFree) {
    freeCta = <Button disabled variant="outline" className="mt-8 w-full h-11 border-slate-300" data-testid="free-current-btn">Current Plan</Button>;
  } else {
    freeCta = <Button disabled variant="outline" className="mt-8 w-full h-11 border-slate-300" data-testid="free-current-btn">Included with Premium</Button>;
  }

  // --- Monthly CTA ---
  let monthlyCta;
  if (!isMember) {
    monthlyCta = (
      <Link to="/register?plan=premium_monthly"><Button className="mt-8 w-full bg-red-600 hover:bg-red-700 text-white h-11" data-testid="monthly-cta-btn">Upgrade Monthly</Button></Link>
    );
  } else if (isMonthly) {
    monthlyCta = <Button disabled className="mt-8 w-full bg-red-600/70 text-white h-11" data-testid="monthly-current-btn">Current Plan</Button>;
  } else if (isAnnual) {
    monthlyCta = <Button disabled={busy} onClick={switchHandler || onSelectMonthly} className="mt-8 w-full bg-red-600 hover:bg-red-700 text-white h-11" data-testid="monthly-switch-btn">{busy ? "Redirecting..." : "Switch to Monthly"}</Button>;
  } else {
    monthlyCta = <Button disabled={busy} onClick={onSelectMonthly} className="mt-8 w-full bg-red-600 hover:bg-red-700 text-white h-11" data-testid="monthly-cta-btn">{busy ? "Redirecting..." : "Upgrade Monthly"}</Button>;
  }

  // --- Annual CTA ---
  let annualCta;
  if (!isMember) {
    annualCta = (
      <Link to="/register?plan=premium_annual"><Button className="mt-8 w-full bg-slate-900 hover:bg-slate-800 text-white h-11" data-testid="annual-cta-btn">Upgrade Annually</Button></Link>
    );
  } else if (isAnnual) {
    annualCta = <Button disabled className="mt-8 w-full bg-slate-900/70 text-white h-11" data-testid="annual-current-btn">Current Plan</Button>;
  } else if (isMonthly) {
    annualCta = <Button disabled={busy} onClick={switchHandler || onSelectAnnual} className="mt-8 w-full bg-slate-900 hover:bg-slate-800 text-white h-11" data-testid="annual-switch-btn">{busy ? "Redirecting..." : "Switch to Annual"}</Button>;
  } else {
    annualCta = <Button disabled={busy} onClick={onSelectAnnual} className="mt-8 w-full bg-slate-900 hover:bg-slate-800 text-white h-11" data-testid="annual-cta-btn">{busy ? "Redirecting..." : "Upgrade Annually"}</Button>;
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch" data-testid="plans-cards">
      {/* FREE */}
      <div className={`bg-white border rounded-md p-8 flex flex-col shadow-sm ${isMember && isFree ? "ring-2 ring-slate-900 border-slate-900" : "border-slate-200"}`} data-testid="plan-free">
        <div className="flex items-center justify-between">
          <div className="text-sm font-semibold text-slate-500 uppercase tracking-widest">Free</div>
          {isMember && isFree && <span className="text-[10px] font-bold bg-slate-900 text-white px-2 py-0.5 rounded uppercase tracking-widest" data-testid="free-current-badge">Current</span>}
        </div>
        <div className="mt-3 flex items-baseline gap-2">
          <span className="text-5xl font-black font-display text-slate-900" data-testid="free-price">$0</span>
          <span className="text-sm text-slate-500">Forever</span>
        </div>
        <div className="text-sm text-slate-500 mt-1">Documentation is always free.</div>
        <ul className="mt-6 space-y-2.5 text-sm text-slate-700 flex-1">
          <li className="flex items-start gap-2"><span className="text-green-600 flex-shrink-0">✓</span><span>Unlimited Workplace Timeline</span></li>
          <li className="flex items-start gap-2"><span className="text-green-600 flex-shrink-0">✓</span><span>Unlimited "Just Left the Office" recordings</span></li>
          <li className="flex items-start gap-2"><span className="text-green-600 flex-shrink-0">✓</span><span>Voice Notes</span></li>
          <li className="flex items-start gap-2"><span className="text-green-600 flex-shrink-0">✓</span><span>Photo &amp; Document Uploads</span></li>
          <li className="flex items-start gap-2"><span className="text-green-600 flex-shrink-0">✓</span><span>Unlimited Workplace Timeline history</span></li>
          <li className="flex items-start gap-2"><span className="text-green-600 flex-shrink-0">✓</span><span>One completed AI-generated grievance</span></li>
          <li className="flex items-start gap-2"><span className="text-green-600 flex-shrink-0">✓</span><span>Basic PDF export</span></li>
        </ul>
        {freeCta}
        {!isMember && <p className="text-xs text-slate-500 mt-3 text-center">No credit card required.</p>}
      </div>

      {/* PREMIUM MONTHLY */}
      <div className={`bg-slate-900 text-white rounded-md p-8 flex flex-col relative shadow-lg ring-2 ring-red-500 md:-mt-4 md:pb-10`} data-testid="plan-monthly">
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-red-600 text-white text-xs font-bold px-3 py-1 rounded uppercase tracking-widest" data-testid="most-popular-badge">
          {isMember && isMonthly ? "Current" : "Most Popular"}
        </span>
        <div className="text-sm font-semibold text-red-400 uppercase tracking-widest">Premium Monthly</div>
        <div className="mt-3 flex items-baseline gap-2">
          <span className="text-5xl font-black font-display" data-testid="monthly-price">$4.95</span>
          <span className="text-lg text-slate-400">per month</span>
        </div>
        <div className="text-sm text-slate-400 mt-1">Cancel anytime.</div>
        <ul className="mt-6 space-y-2.5 text-sm text-slate-200 flex-1">
          <li className="text-xs font-semibold text-red-400 uppercase tracking-widest">Everything in Free plus:</li>
          <li className="flex items-start gap-2"><span className="text-red-400 flex-shrink-0">✓</span><span>Unlimited grievance creation</span></li>
          <li className="flex items-start gap-2"><span className="text-red-400 flex-shrink-0">✓</span><span>Advanced AI guidance</span></li>
          <li className="flex items-start gap-2"><span className="text-red-400 flex-shrink-0">✓</span><span>Enhanced contract analysis</span></li>
          <li className="flex items-start gap-2"><span className="text-red-400 flex-shrink-0">✓</span><span>Unlimited case organization</span></li>
          <li className="flex items-start gap-2"><span className="text-red-400 flex-shrink-0">✓</span><span>Future Premium features</span></li>
          <li className="flex items-start gap-2"><span className="text-red-400 flex-shrink-0">✓</span><span>Priority support</span></li>
        </ul>
        {monthlyCta}
        <p className="text-xs text-slate-500 mt-3 text-center">Cancel anytime.</p>
      </div>

      {/* PREMIUM ANNUAL */}
      <div className={`bg-white border rounded-md p-8 flex flex-col shadow-sm ${isMember && isAnnual ? "ring-2 ring-slate-900 border-slate-900" : "border-slate-200"}`} data-testid="plan-annual">
        <div className="flex items-center justify-between">
          <div className="text-sm font-semibold text-slate-500 uppercase tracking-widest">Premium Annual</div>
          {isMember && isAnnual && <span className="text-[10px] font-bold bg-slate-900 text-white px-2 py-0.5 rounded uppercase tracking-widest" data-testid="annual-current-badge">Current</span>}
        </div>
        <div className="mt-3 flex items-baseline gap-2">
          <span className="text-5xl font-black font-display text-slate-900" data-testid="annual-price">$49.95</span>
          <span className="text-lg text-slate-500">per year</span>
        </div>
        <div className="mt-2 inline-flex items-center gap-1.5" data-testid="annual-savings">
          <span className="inline-block bg-green-100 text-green-800 border border-green-200 text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-widest">Save {savingsPct}%</span>
          <span className="text-xs text-slate-500">≈ ${(49.95/12).toFixed(2)}/mo</span>
        </div>
        <ul className="mt-6 space-y-2.5 text-sm text-slate-700 flex-1">
          <li className="text-xs font-semibold text-slate-500 uppercase tracking-widest">Everything in Premium Monthly plus:</li>
          <li className="flex items-start gap-2"><span className="text-red-600 flex-shrink-0">✓</span><span>Best annual value</span></li>
          <li className="flex items-start gap-2"><span className="text-red-600 flex-shrink-0">✓</span><span>Lower effective monthly cost</span></li>
          <li className="flex items-start gap-2"><span className="text-red-600 flex-shrink-0">✓</span><span>All future Premium features</span></li>
        </ul>
        {annualCta}
        <p className="text-xs text-slate-500 mt-3 text-center">Billed annually.</p>
      </div>

      {isMember && error && (
        <p className="md:col-span-3 text-sm text-red-600 text-center" data-testid="billing-error">{error}</p>
      )}
    </div>
  );
}

export default PricingCards;
